import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Menu,
  Bell,
  Sun,
  Users
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { useAuthContext } from "@/context/authentication/auth-context";
import { useNotifications, useUnreadCount, useMarkAllAsRead } from "@/services/queries/useNotifications";
import { useLogout } from "@/services/queries/useAuth";
import { UserDropdownMenu } from "./user-dropdown-menu";
import { HeaderProjectSelector } from "./header-project-selector";
import { NotificationPopoverContent } from "../notificacoes/notification-popover-content";
import { useProjetoContext } from "@/context/projeto";
import InputSearch from "../ui/input-search";

interface HeaderProps {
  onSidebarToggle?: () => void;
}

export default function Header({ onSidebarToggle }: HeaderProps) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [, setLocation] = useLocation();

  // Auth context
  const { person, isLoading: isLoadingUser } = useAuthContext();

  // Project context
  const {
    projetos,
    projetoSelecionado,
    selecionarProjeto,
    isLoading: isLoadingProjects,
  } = useProjetoContext();

  // Notifications
  const { data: notifications, isLoading: isLoadingNotifications } = useNotifications();
  const { data: unreadData } = useUnreadCount();
  const markAllAsReadMutation = useMarkAllAsRead();
  const logoutMutation = useLogout();

  const getUserName = () => {
    if (isLoadingUser || !person) return "Usuário";
    return person.name || "Usuário";
  };

  const getUserInitials = () => {
    if (isLoadingUser || !person) return "U";
    const name = person.name || "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Get user avatar URL from person customization
  const getUserAvatar = (): string | null => {
    // Avatar URL would be in person.custom.url_avatar based on types/person.ts
    // This may need backend integration to return custom data
    return null;
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleClearAll = () => {
    markAllAsReadMutation.mutate();
  };

  const handleViewAll = () => {
    setNotificationsOpen(false);
    setLocation("/notificacoes");
  };

  const handleThemeToggle = () => {
    // TODO: Implement theme toggle functionality
    console.log("Theme toggle clicked");
  };

  const handleUserSettings = () => {
    setLocation("/configuracoes");
  };

  const handleMenuToggle = () => {
    if (onSidebarToggle) {
      onSidebarToggle();
    }
  };

  const unreadCount = unreadData?.count || 0;

  return (
    <header className="h-12 bg-darkblue rounded-lg flex items-center justify-between px-3">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          {/* Left section - Menu */}
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMenuToggle}
              aria-label="Toggle Menu"
              className="text-white hover:bg-white/10 h-8 w-8"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>

          {/* Center section - Search */}
          <div className="ml-3">
            <div className="relative">
              <InputSearch
                value={searchValue}
                onChange={setSearchValue}
                positionIcon="end"
                className="w-0 text-sm"
                classNameIcon="text-primary"
              />
            </div>
          </div>
        </div>

        {/* Right section - Actions and user */}
        <div className="flex items-center gap-1">
          {/* Project Selector */}
          <HeaderProjectSelector
            projects={projetos}
            selectedProject={projetoSelecionado}
            onProjectChange={(project) => selecionarProjeto(project)}
            isLoading={isLoadingProjects}
          />

          {/* Users Settings */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleUserSettings}
            aria-label="User settings"
            className="text-gray-300 hover:bg-white/10 hover:text-white p-0 h-auto w-auto"
          >
            <Users className="h-6 w-6" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleThemeToggle}
            aria-label="Toggle theme"
            className="text-white hover:bg-white/10 h-8 w-8"
          >
            <Sun className="h-4 w-4" />
          </Button>

          {/* Notifications */}
          <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-white hover:bg-white/10 h-8 w-8"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 h-3.5 w-3.5 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-semibold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 bg-[#15203C] border-0.5 border-gray-600 p-0"
            >
              <NotificationPopoverContent
                notifications={notifications || []}
                isLoading={isLoadingNotifications}
                onClearAll={handleClearAll}
                onViewAll={handleViewAll}
              />
            </PopoverContent>
          </Popover>

          {/* User profile dropdown */}
          <UserDropdownMenu
            userName={getUserName()}
            userInitials={getUserInitials()}
            userAvatar={getUserAvatar()}
            onLogout={handleLogout}
            isLoggingOut={logoutMutation.isPending}
          />
        </div>
      </div>
    </header>
  );
}
